<template>
  <v-card>
    <v-card-title>
      <div class="text-h6 align-baseline text-center full-width">
        Replace
        <strong>{{ node.name }}</strong>
        <v-btn icon class="close-button elevation-0" @click="cancelDialog">
          <v-icon>mdi-close-circle</v-icon>
        </v-btn>
        <v-divider class="my-2"></v-divider>
      </div>
    </v-card-title>
    <v-card-text>
      <v-form class="mx-2" ref="form">
        <v-row v-if="newNode.name">
          <v-col cols="12">
            <v-text-field
              label="New Category Name"
              v-model="newNode.name"
              :rules="[() => !!newNode.name || 'An entry is required']"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row v-if="newNode.meta.type">
          <v-col cols="12">
            <v-select
              v-model="newNode.meta.type"
              :items="allowedTypes"
              item-value="type"
              item-text="name"
              chips
              class="white--text"
              hint="Select type for the new category"
            ></v-select>
          </v-col>
        </v-row>
        <v-row v-if="newNode.name">
          <v-col cols="12">
            <TypeCreator
              :node="newNode"
              :selectedRecord="selectedRecord"
              @validation="canSubmit = $event"
              :createMode="true"
            />
          </v-col>
        </v-row>
        <v-row v-if="node.parent && newNode.name">
          <v-col>
            <conditionalCreator
              :node="newNode"
              :parentNodeId="node.parent"
              :selectedRecord="selectedRecord"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions class="pa-4 justify-end">
      <v-btn color="primary" @click="submitChanges" :disabled="!canSubmit">
        <v-progress-circular
          v-if="isSubmitting"
          class="mr-3"
          indeterminate
          :size="20"
          :width="1"
          color="secondary"
        ></v-progress-circular>
        Replace
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
<script>
import { mapGetters } from "vuex";
import TypeCreator from "../../components/TypeCreator.vue";
import conditionalCreator from "../record-navigator/add-categories/conditionalCreator.vue";
import cloneDeep from "lodash/cloneDeep";
import { replaceNodeInRecord } from "../../../utils/api";

export default {
  props: {
    node: Object,
    selectedRecord: Object
  },
  components: { TypeCreator, conditionalCreator },
  name: "ReplaceCategory",
  created() {
    this.allowedTypes = [
      { name: "Title", type: "title" },
      { name: "Text", type: "text" },
      { name: "Yes/No", type: "boolean" },
      { name: "Numeric", type: "numeric" },
      { name: "Numeric Scale", type: "numeric-scale" },
      { name: "Numeric Array", type: "numeric-array" },
      { name: "Text Array", type: "text-array" },
      { name: "Single Choice List", type: "value-list" },
      { name: "Multiple Value List", type: "multiple-value-list" },
      { name: "Time", type: "time" },
      { name: "Date", type: "np-date" },
      { name: "Non Periodic Text", type: "np-text" }
    ];
    const fakeNode = {
      name: this.node.name,
      meta: cloneDeep(this.node.meta)
    };
    const uom = this.units.find(item => item.uom === fakeNode.meta.uom);
    fakeNode.meta.uom = uom;
    this.newNode = Object.assign({}, fakeNode);
  },
  data() {
    return {
      search: "",
      isSubmitting: false,
      canSubmit: false,
      newNode: {}
    };
  },
  computed: {
    ...mapGetters("allTypes", { allTypes: "list" }, { root: true }),
    ...mapGetters("allUoms", { units: "list" }, { root: true })
  },
  methods: {
    cancelDialog() {
      // Put up an "Are you sure?" dialog of some sort
      if (!window.confirm("Are you sure you want to cancel?")) {
        return;
      }
      this.$emit("cancel-edit-dialog");
    },
    async submitChanges() {
      this.isSubmitting = true;
      if (this.newNode.meta.uom?.new) {
        // We have to wait for the new Unit of Measure to get added before we procceed
        this.addNewUoms();
      }
      // Use just the uom name
      if (this.newNode.meta.uom?.uom) {
        this.newNode.meta.uom = this.newNode.meta.uom.uom;
      }

      // set up the new category
      const data = this.setupNewCategory();

      // Now replace the old categeory with the new one
      const params = {
        rec_id: this.selectedRecord._id,
        node_id: this.node._id
      };
      const payload = {
        new_node: data
      };
      const response = await replaceNodeInRecord(params, payload);

      if (response) {
        const result = response?.Success;
        this.$store.dispatch("addNotification", {
          type: result ? "success" : "errorMessage",
          message: `${result ? "Replaced" : "Could not replace"} category ${
            this.node.name
          } with ${this.newNode.name}`
        });
        if (result) {
          // Close any open tabs with the former node
          this.$store.state.records.tabs
            .filter(tab => {
              return tab.recordItemId === this.node._id;
            })
            .map(tab => {
              this.$store.dispatch("closeTab", tab);
            });

          // Update the store with the new node data
          await this.$store.dispatch("updateRecordFromData", result);
        }
      }
      this.isSubmitting = false;
      this.$emit("cancel-edit-dialog");
    },
    async addNewUoms() {
      // Create the new unit of measure
      const payload = {
        name: this.newNode.meta.uom.uom,
        ...this.newNode.meta.uom
      };
      delete payload.new;
      // Create the new uom
      const result = await this.$store.dispatch(
        "postNewUom/createUom",
        payload
      );
      this.$store.dispatch("addNotification", {
        type: result ? "success" : "errorMessage",
        message: `${result ? "Added" : "Could not add"} new unit of measure ${
          payload.uom
        } `
      });
      if (result) {
        // Update the store with the new uom
        this.$store.commit("allUoms/updateUoms", result);
      }
    },
    setupNewCategory() {
      const now = new Date().toISOString();

      /* Create the new categories */
      const payload = {
        name: this.newNode.name,
        _id: "",
        child_count: 0,
        children: [],
        value: "",
        meta: {
          created: new Date().toISOString(),
          type: this.newNode.meta.type,
          periodic: true,
          periodic_group: this.newNode.meta.periodic_group,
          "condition-type": this.newNode.meta["condition-type"],
          "condition-node": this.newNode.meta["condition-node"],
          condition: this.newNode.meta.condition,
          question: this.newNode.meta.question,
          comment: this.newNode.meta.comment,
          tooltip: this.newNode.meta.tooltip
        }
      };
      // Add type-specific attributes
      switch (this.newNode.meta.type) {
        case "text":
          payload.value = "";
          break;
        case "boolean":
          payload.meta.choices = ["Yes", "No"];
          payload.value = "No";
          break;
        case "numeric":
          payload.meta.precision = this.newNode.meta.precision;
          payload.meta.uom = this.newNode.meta.uom;
          payload.meta.min = Number.isFinite(this.newNode.meta.min)
            ? parseFloat(this.newNode.meta.min, 10)
            : null;
          payload.meta.max = Number.isFinite(this.newNode.meta.max)
            ? parseFloat(this.newNode.meta.max, 10)
            : null;
          payload.value = this.newNode.meta.min
            ? parseFloat(this.newNode.meta.min, 10)
            : 0;
          break;
        case "numeric-scale":
          payload.meta.precision = this.newNode.meta.precision;
          payload.meta.uom = this.newNode.meta.uom;
          payload.meta.min = parseFloat(this.newNode.meta.min, 10);
          payload.meta.max = parseFloat(this.newNode.meta.max, 10);
          payload.meta.step = parseFloat(this.newNode.meta.step, 10);
          payload.value = parseFloat(this.newNode.meta.min, 10);
          break;
        case "numeric-array":
          payload.meta.precision = this.newNode.meta.precision;
          payload.meta.uom = this.newNode.meta.uom;
          payload.meta.min = Number.isFinite(this.newNode.meta.min)
            ? parseFloat(this.newNode.meta.min, 10)
            : null;
          payload.meta.max = Number.isFinite(this.newNode.meta.max)
            ? parseFloat(this.newNode.meta.max, 10)
            : null;
          payload.meta.separator = this.newNode.meta.separator;
          payload.meta.value_name = this.newNode.meta.value_name.concat();
          payload.value = this.newNode.value;
          break;
        case "text-array":
          payload.meta.separator = this.newNode.meta.separator;
          payload.meta.value_name = this.newNode.meta.value_name.concat();
          payload.value = this.newNode.value;
          break;
        case "value-list":
          payload.meta.choices = this.newNode.meta.choices.concat();
          payload.value = null;
          break;
        case "multiple-value-list":
          payload.meta.choices = this.newNode.meta.choices.concat();
          payload.value = this.newNode.value;
          break;
        case "np-date":
          payload.meta.periodic = false;
          break;
        case "np-text":
          payload.meta.periodic = false;
          break;
        case "np-phonenumber":
          payload.meta.periodic = false;
          break;
      }
      // If this is a periodic entry, change the value to be an array of object(s)
      // Everything is periodic now so always do this.
      if (payload.meta.periodic) {
        const theValue = Array.isArray(payload.value)
          ? payload.value
          : [payload.value];
        payload.value = [];
      }

      // If the UOM is an object, convert it to just the uom name
      if (payload.meta.uom) {
        payload.meta.uom = payload.meta.uom.uom
          ? payload.meta.uom.uom
          : payload.meta.uom;
      }

      return payload;
    },
    useFriendlyTypeName(type) {
      const theType = this.allowedTypes.find(item => item.type === type);
      return theType.name;
    }
  }
};
</script>

<style scoped lang="scss">
.close-button {
  position: absolute;
  right: 0px;
  cursor: pointer;
  z-index: 200;
  margin-top: -10px;
}
.v-stepper__content {
  padding-top: 0;
}
.full-width {
  width: 100%;
}
</style>
